import { 
  collection, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const blockedDaysCollection = 'blockedDays';

// Get all blocked days
export const getBlockedDays = async () => {
  const blockedDaysRef = collection(db, blockedDaysCollection);
  const q = query(blockedDaysRef, orderBy('date', 'asc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt)
    };
  });
};

// Get blocked days within a date range
export const getBlockedDaysInRange = async (startDate, endDate) => {
  const blockedDaysRef = collection(db, blockedDaysCollection);
  
  // Convert dates to string format for comparison
  const startDateStr = new Date(startDate).toISOString().split('T')[0];
  const endDateStr = new Date(endDate).toISOString().split('T')[0];
  
  const q = query(
    blockedDaysRef,
    where('date', '>=', startDateStr),
    where('date', '<=', endDateStr),
    orderBy('date', 'asc')
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get blocked days for specific location
export const getBlockedDaysByLocation = async (location) => {
  const blockedDaysRef = collection(db, blockedDaysCollection);
  const q = query(
    blockedDaysRef,
    where('location', 'in', [location, 'All']),
    orderBy('date', 'asc')
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Create a new blocked day
export const createBlockedDay = async (blockedDayData) => {
  try {
    const blockedDaysRef = collection(db, blockedDaysCollection);
    
    // Ensure date is in ISO format (YYYY-MM-DD)
    const dateObj = new Date(blockedDayData.date);
    const isoDate = dateObj.toISOString().split('T')[0];
    
    const newBlockedDay = {
      ...blockedDayData,
      date: isoDate,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(blockedDaysRef, newBlockedDay);
    return docRef.id;
  } catch (error) {
    console.error('Error creating blocked day:', error);
    throw error;
  }
};

// Create multiple blocked days (for date ranges)
export const createBlockedDayRange = async (startDate, endDate, blockedDayData) => {
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const blockedDayIds = [];
    
    // Create blocked day for each date in the range
    for (let currentDate = new Date(start); currentDate <= end; currentDate.setDate(currentDate.getDate() + 1)) {
      const isoDate = currentDate.toISOString().split('T')[0];
      
      const dayData = {
        ...blockedDayData,
        date: isoDate
      };
      
      const id = await createBlockedDay(dayData);
      blockedDayIds.push(id);
    }
    
    return blockedDayIds;
  } catch (error) {
    console.error('Error creating blocked day range:', error);
    throw error;
  }
};

// Update a blocked day
export const updateBlockedDay = async (id, updatedData) => {
  try {
    const blockedDayRef = doc(db, blockedDaysCollection, id);
    
    // Ensure date is in ISO format if being updated
    if (updatedData.date) {
      const dateObj = new Date(updatedData.date);
      updatedData.date = dateObj.toISOString().split('T')[0];
    }
    
    const updatePayload = {
      ...updatedData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(blockedDayRef, updatePayload);
    return true;
  } catch (error) {
    console.error('Error updating blocked day:', error);
    throw error;
  }
};

// Delete a blocked day
export const deleteBlockedDay = async (id) => {
  try {
    const blockedDayRef = doc(db, blockedDaysCollection, id);
    await deleteDoc(blockedDayRef);
    return true;
  } catch (error) {
    console.error('Error deleting blocked day:', error);
    throw error;
  }
};

// Get a single blocked day by ID
export const getBlockedDay = async (id) => {
  try {
    const blockedDayRef = doc(db, blockedDaysCollection, id);
    const docSnap = await getDoc(blockedDayRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt)
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting blocked day:', error);
    throw error;
  }
};

// Check if a specific date is blocked
export const isDateBlocked = async (date, location = 'All') => {
  try {
    const dateStr = new Date(date).toISOString().split('T')[0];
    const blockedDaysRef = collection(db, blockedDaysCollection);
    
    const q = query(
      blockedDaysRef,
      where('date', '==', dateStr),
      where('location', 'in', [location, 'All'])
    );
    
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking if date is blocked:', error);
    throw error;
  }
};

// Get blocked dates as array of date strings (for date picker exclusion)
export const getBlockedDatesArray = async (startDate, endDate, location = 'All') => {
  try {
    const blockedDays = await getBlockedDaysInRange(startDate, endDate);
    
    // Filter by location if specified
    const filteredDays = blockedDays.filter(day => 
      day.location === 'All' || day.location === location
    );
    
    return filteredDays.map(day => day.date);
  } catch (error) {
    console.error('Error getting blocked dates array:', error);
    throw error;
  }
};

// Delete multiple blocked days by date range
export const deleteBlockedDayRange = async (startDate, endDate) => {
  try {
    const blockedDays = await getBlockedDaysInRange(startDate, endDate);
    
    const deletePromises = blockedDays.map(day => deleteBlockedDay(day.id));
    await Promise.all(deletePromises);
    
    return blockedDays.length;
  } catch (error) {
    console.error('Error deleting blocked day range:', error);
    throw error;
  }
};